// Darstellungsregeln der Übergabeprotokolle (design_handoff_uebergabe) — rein, ohne React/DB.
// Setzt sich ausschließlich aus den vorhandenen Regeln in lib/uebergabe/* zusammen (dort nichts geändert):
// welche Chips und Aktionen eine Listenkarte zeigt, Art-Zähler, Raum-Unterzeile, Schlüssel-Summe.
import type { Art, Pruefpunkt, RaumBlock, SchluesselZeile } from '../uebergabe/typen';
import type { Zeilenzustand } from '../uebergabe/listenzeilen';
import { ermittleLoeschAktion, geraeteHinweis, istEntwurfsZustand } from '../uebergabe/zeilenanzeige';

/** Art-Farben (Handoff §2): Einzug grün, Auszug orange, Übergabe an Eigentümer blau. */
export const ART_FARBE: Record<Art, string> = { einzug: '#34c759', auszug: '#ff9500', kauf: '#0a84ff' };
export const ART_LABEL: Record<Art, string> = { einzug: 'Einzug', auszug: 'Auszug', kauf: 'Übergabe an Eigentümer' };

export type ArtOderAlle = Art | 'alle';

/** Zähler je Art über die aktuelle Status-/Such-Auswahl (Art-Widgets = Filter UND Zähler). */
export function zaehleArten(liste: readonly { art: Art }[]): Record<ArtOderAlle, number> {
  const z: Record<ArtOderAlle, number> = { alle: liste.length, einzug: 0, auszug: 0, kauf: 0 };
  for (const e of liste) if (e.art in z) z[e.art] += 1;
  return z;
}

export type StatusChip = 'entwurf' | 'abgeschlossen' | 'abgebrochen';
export type InfoChip = 'versendet' | 'pdf_fehlt' | 'nicht_versendet' | 'keine_adresse' | 'im_dms';
export type KartenAktion =
  | 'weiter' | 'senden' | 'hier_weiter'
  | 'erneut_erzeugen' | 'mail_erneut' | 'pdf' | 'ins_dms' | 'als_vorlage'
  | 'verwerfen' | 'abbrechen' | 'entwurf_loeschen';

export interface KartenEingabe {
  zustand: Zeilenzustand;
  hatDatensatz: boolean;
  hatEntwurf: boolean;
  entwurfVeraltet: boolean;
  pdf_path: string | null;
  mail_versendet_am: string | null;
  drive_file_id: string | null;
  /** Mindestens eine Vertragspartei (Übergeber/Übernehmer) hat eine E-Mail-Adresse */
  hatEmailAdresse: boolean;
  /** Ergebnis von pruefeAbschlussBereit für den lokalen Entwurf (null ohne Entwurf) */
  bereitschaft: { bereit: boolean; grund: string | null } | null;
}

export interface KartenAnsicht {
  status: StatusChip;
  /** Gerätehinweis bei Entwürfen („auf diesem Gerät“ …), sonst null */
  geraet: string | null;
  chips: InfoChip[];
  /** In der Reihenfolge, in der sie angezeigt werden */
  aktionen: KartenAktion[];
  /** Laufende Erfassung (Seit …/Hausmeister-Freigabe) */
  inErfassung: boolean;
  /** „Jetzt senden“ gesperrt (Entwurf noch nicht bereit) */
  sendenGesperrt: boolean;
  /** Hinweis im Info-Feld, warum noch nicht gesendet werden kann */
  bereitGrund: string | null;
}

/**
 * Chips und Aktionen einer Listenkarte — dieselben Bedingungen wie bisher in UebergabeZeileKarte,
 * nur als Liste: erst die Entwurfs-Aktionen, dann die eines abgeschlossenen Protokolls, zuletzt die
 * unwiderruflichen (Verwerfen/Abbrechen/Löschen).
 */
export function kartenAnsicht(z: KartenEingabe): KartenAnsicht {
  const abgeschlossen = z.zustand === 'abgeschlossen';
  const pdfFehlt = abgeschlossen && !z.pdf_path;
  const mailOffen = abgeschlossen && !z.mail_versendet_am;
  const inErfassung = z.zustand === 'lokal_in_bearbeitung' || z.zustand === 'in_erfassung_fremd';
  const entwurf = istEntwurfsZustand(z.zustand);

  const chips: InfoChip[] = [];
  const aktionen: KartenAktion[] = [];

  if (z.hatEntwurf && !z.entwurfVeraltet) aktionen.push('weiter', 'senden');
  if (z.zustand === 'in_erfassung_fremd' && !z.hatEntwurf) aktionen.push('hier_weiter');

  if (abgeschlossen && !mailOffen) chips.push('versendet');
  if (pdfFehlt) { chips.push('pdf_fehlt'); aktionen.push('erneut_erzeugen'); }
  if (mailOffen && !pdfFehlt) {
    if (z.hatEmailAdresse) { chips.push('nicht_versendet'); aktionen.push('mail_erneut'); }
    else chips.push('keine_adresse');
  }
  if (abgeschlossen && z.pdf_path) {
    aktionen.push('pdf');
    if (z.drive_file_id) chips.push('im_dms'); else aktionen.push('ins_dms');
  }
  if (abgeschlossen) aktionen.push('als_vorlage');

  if (z.entwurfVeraltet) aktionen.push('verwerfen');
  const loesch = ermittleLoeschAktion({ zustand: z.zustand, hatDatensatz: z.hatDatensatz });
  if (loesch === 'server_stornieren') aktionen.push('abbrechen');
  if (loesch === 'lokal_loeschen') aktionen.push('entwurf_loeschen');

  return {
    status: entwurf ? 'entwurf' : (z.zustand as 'abgeschlossen' | 'abgebrochen'),
    geraet: entwurf ? geraeteHinweis(z.zustand) : null,
    chips,
    aktionen,
    inErfassung,
    sendenGesperrt: !z.bereitschaft?.bereit,
    bereitGrund: z.hatEntwurf && !z.entwurfVeraltet && z.bereitschaft && !z.bereitschaft.bereit ? z.bereitschaft.grund : null,
  };
}

/** Unterzeile einer Raumkarte: „n Mängel · x/y geprüft · n Fotos“ (Fotos nur wenn vorhanden). */
export function raumUnterzeile(raum: Pick<RaumBlock, 'pruefpunkte' | 'fotos'>): { text: string; ton: 'bad' | 'ok' | 'neutral'; maengel: number; geprueft: number } {
  const pp: Pruefpunkt[] = Array.isArray(raum.pruefpunkte) ? raum.pruefpunkte : [];
  const maengel = pp.filter((p) => p?.status === 'mangel').length;
  const geprueft = pp.filter((p) => p?.status === 'io' || p?.status === 'mangel').length;
  const fotos = (Array.isArray(raum.fotos) ? raum.fotos.length : 0)
    + pp.reduce((s, p) => s + (Array.isArray(p?.fotos) ? p.fotos.length : 0), 0);
  const teile = [maengel === 1 ? '1 Mangel' : `${maengel} Mängel`, `${geprueft}/${pp.length} geprüft`];
  if (fotos > 0) teile.push(fotos === 1 ? '1 Foto' : `${fotos} Fotos`);
  const ton = maengel > 0 ? 'bad' : pp.length > 0 && geprueft === pp.length ? 'ok' : 'neutral';
  return { text: teile.join(' · '), ton, maengel, geprueft };
}

/** Summe aller übergebenen Schlüssel (Anzeige „n Schlüssel“). */
export function schluesselSumme(liste: readonly Pick<SchluesselZeile, 'anzahl'>[] | null | undefined): number {
  return (liste ?? []).reduce((s, z) => s + (Number.isFinite(z?.anzahl) && z.anzahl > 0 ? z.anzahl : 0), 0);
}

export type SchluesselSymbol = 'haustuer' | 'wohnungstuer' | 'keller' | 'briefkasten' | 'garage' | 'sonstig';

/** Symbol je Schlüsselart (Handoff §4.4): nur die Standardarten aus SCHLUESSEL_STANDARD haben ein eigenes, alles andere „Schlüssel“. */
export function schluesselSymbol(bezeichnung: string): SchluesselSymbol {
  switch (bezeichnung.trim().toLowerCase()) {
    case 'haustür': return 'haustuer';
    case 'wohnungstür': return 'wohnungstuer';
    case 'keller': return 'keller';
    case 'briefkasten': return 'briefkasten';
    case 'garage': return 'garage';
    default: return 'sonstig';
  }
}

export type RaumSymbol = 'flur' | 'wohnen' | 'kueche' | 'bad' | 'wc' | 'schlafen' | 'kind' | 'balkon' | 'keller' | 'abstell' | 'dach' | 'garage' | 'sonstig';

/** Symbol und Farbe eines Raums (Handoff §4.2, Farben aus dem Prototyp) — über Stichwörter, weil der
 *  Katalog aus der DB kommt („Flur/Diele“, „Balkon/Terrasse“ …). Nur Anzeige. */
export function raumStil(raum: string): { symbol: RaumSymbol; farbe: string } {
  const r = raum.toLowerCase();
  if (r.includes('flur') || r.includes('diele')) return { symbol: 'flur', farbe: '#8e8e93' };
  if (r.includes('wohn')) return { symbol: 'wohnen', farbe: '#0a84ff' };
  if (r.includes('küche') || r.includes('kueche')) return { symbol: 'kueche', farbe: '#ff9500' };
  if (r.includes('wc')) return { symbol: 'wc', farbe: '#32ade6' };
  if (r.includes('bad')) return { symbol: 'bad', farbe: '#32ade6' };
  if (r.includes('schlaf')) return { symbol: 'schlafen', farbe: '#5856d6' };
  if (r.includes('kind')) return { symbol: 'kind', farbe: '#ff2d55' };
  if (r.includes('balkon') || r.includes('terrasse') || r.includes('loggia')) return { symbol: 'balkon', farbe: '#ffcc00' };
  if (r.includes('keller')) return { symbol: 'keller', farbe: '#a2845e' };
  if (r.includes('abstell')) return { symbol: 'abstell', farbe: '#a2845e' };
  if (r.includes('dach')) return { symbol: 'dach', farbe: '#8e8e93' };
  if (r.includes('garage') || r.includes('stellplatz')) return { symbol: 'garage', farbe: '#636366' };
  if (r.includes('sonstig')) return { symbol: 'sonstig', farbe: '#34c759' };
  return { symbol: 'sonstig', farbe: '#8e8e93' };
}

export type ZaehlerSymbol = 'strom' | 'kaltwasser' | 'warmwasser' | 'waerme' | 'gas' | 'sonstig';

/** Symbol und Farbe eines Zählers (Handoff §4.3: Strom gelb, Kaltwasser blau, Warmwasser rot,
 *  Heizung/Wärme orange, Gas orange, sonst grau) — der Typ ist Freitext, daher Stichwörter. */
export function zaehlerStil(typ: string | null | undefined): { symbol: ZaehlerSymbol; farbe: string } {
  const t = (typ ?? '').toLowerCase();
  if (t.includes('strom') || t.includes('elektr')) return { symbol: 'strom', farbe: '#ffcc00' };
  if (t.includes('warm')) return { symbol: 'warmwasser', farbe: '#ff3b30' };
  if (t.includes('kalt') || t.includes('wasser')) return { symbol: 'kaltwasser', farbe: '#32ade6' };
  if (t.includes('heiz') || t.includes('wärme') || t.includes('waerme')) return { symbol: 'waerme', farbe: '#ff9500' };
  if (t.includes('gas')) return { symbol: 'gas', farbe: '#ff9500' };
  return { symbol: 'sonstig', farbe: '#8e8e93' };
}
